/**
 * Utilitários de URL amigável para SEO de imóveis.
 */

import { getPropertyTypeLabel, getPropertyTypeBySlug } from '../../constants/propertyTypes';

/** IDs automáticos do Firestore: 20 chars alfanuméricos (sem hífen). */
export const FIRESTORE_AUTO_ID_REGEX = /^[A-Za-z0-9]{20}$/;

/**
 * Gera slug SEO a partir de um texto (título do imóvel, cidade, etc.).
 * Minúsculas, sem acentos, sem caracteres especiais, espaços → hífens.
 */
export function generateSlug(text) {
  if (!text) return '';
  return String(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Alias histórico — preferir generateSlug em código novo. */
export const slugify = generateSlug;

/**
 * Extrai o ID do documento a partir de `/propriedades/:slugAndId`.
 * Aceita apenas o formato `{slug}-{id}` (ex.: apto-centro-5AVIFEasS1goaWBzDWHB).
 * URLs só com ID (`/propriedades/{id}`) não são mais válidas.
 */
export function extractPropertyIdFromParam(segment) {
  const s = String(segment || '').trim();
  if (!s) return null;
  // Formato legado só-ID: rejeitado de propósito (sem redirect)
  if (FIRESTORE_AUTO_ID_REGEX.test(s)) return null;
  const idx = s.lastIndexOf('-');
  if (idx <= 0) return null;
  const slugPart = s.slice(0, idx);
  const id = s.slice(idx + 1);
  if (!slugPart || !FIRESTORE_AUTO_ID_REGEX.test(id)) return null;
  return id;
}

/** True quando o segmento é só o ID Firestore (URL antiga inválida). */
export function isLegacyPropertyIdOnlySegment(segment) {
  return FIRESTORE_AUTO_ID_REGEX.test(String(segment || '').trim());
}

/**
 * Segmento de URL: sempre `{slug}-{id}` (slug fallback: "imovel").
 * @param {string|{id?: string, title?: string, code?: string}} property
 */
export function buildPropertySlugSegment(property) {
  const id = typeof property === 'string' ? property : property?.id;
  if (!id) return '';
  const title =
    typeof property === 'object' && property
      ? property.title || property.code || ''
      : '';
  const slug = generateSlug(title) || 'imovel';
  return `${slug}-${id}`;
}

/** Caminho relativo: `/propriedades/{slug}-{id}` */
export function buildPropertyPath(property) {
  const segment = buildPropertySlugSegment(property);
  return segment ? `/propriedades/${segment}` : '/propriedades';
}

export function getBaseUrl(fallbackOrigin) {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return fallbackOrigin || '';
}

/**
 * URL absoluta do detalhe do imóvel.
 * Aceita objeto `{ id, title }` ou string id (legado).
 */
export function buildPropertyUrl(property, baseUrl) {
  const base = (baseUrl || getBaseUrl()).replace(/\/$/, '');
  return `${base}${buildPropertyPath(property)}`;
}

export function buildCityListingUrl(city, baseUrl) {
  const base = (baseUrl || getBaseUrl()).replace(/\/$/, '');
  return `${base}/propriedades/${slugify(city)}`;
}

export function buildNeighborhoodListingUrl(city, neighborhood, baseUrl) {
  const base = (baseUrl || getBaseUrl()).replace(/\/$/, '');
  return `${base}/propriedades/${slugify(city)}/${slugify(neighborhood)}`;
}

export function buildTypeListingUrl(type, baseUrl) {
  const base = (baseUrl || getBaseUrl()).replace(/\/$/, '');
  return `${base}/propriedades/tipo/${slugify(type)}`;
}

/**
 * Parâmetros de filtro que NÃO fazem parte do caminho amigável (cidade/bairro/tipo)
 * e devem ir para a query string.
 */
const EXTRA_QUERY_KEYS = [
  'search', 'purpose', 'minPrice', 'maxPrice', 'bedrooms',
  'suites', 'bathrooms', 'parkingSpaces', 'minArea', 'maxArea', 'orderBy',
];

export function buildListingQueryString(filters = {}, page = 1, { includePathKeys = false } = {}) {
  const params = new URLSearchParams();
  const keys = includePathKeys
    ? [...EXTRA_QUERY_KEYS, 'type', 'location', 'neighborhood']
    : EXTRA_QUERY_KEYS;

  keys.forEach((key) => {
    const value = filters[key];
    if (value != null && String(value).trim() !== '') {
      params.set(key, String(value).trim());
    }
  });

  if (page > 1) params.set('page', String(page));

  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// Compat: mantém assinatura antiga usada em outros pontos.
export function buildListingSearchParams(filters, page = 1) {
  return buildListingQueryString(filters, page, { includePathKeys: true });
}

/**
 * Caminho amigável baseado em cidade [/ bairro] [/ tipo].
 * Estrutura: /propriedades/{cidade}[/{bairro}][/{tipo}]
 * Tipo global sem cidade: /propriedades/tipo/{tipo}
 */
export function buildListingPrettyPath(filters = {}) {
  const city = filters.location;
  const neighborhood = filters.neighborhood
    ? String(filters.neighborhood).split(',')[0].trim()
    : '';
  const typeSlug = filters.type ? slugify(getPropertyTypeLabel(filters.type)) : '';

  if (!city) {
    if (typeSlug) return `/propriedades/tipo/${typeSlug}`;
    return '/propriedades';
  }

  const segments = ['/propriedades', slugify(city)];
  if (neighborhood) segments.push(slugify(neighborhood));
  if (typeSlug) segments.push(typeSlug);
  return segments.join('/');
}

export function buildListingCanonicalPath(filters = {}, page = 1) {
  const pretty = buildListingPrettyPath(filters);
  return `${pretty}${buildListingQueryString(filters, page)}`;
}

/** Caminho completo para navegação no React Router (path + query). */
export function buildListingRoutePath(filters = {}, page = 1) {
  return buildListingCanonicalPath(filters, page);
}

const LISTING_QUERY_FILTER_KEYS = [
  'search', 'purpose', 'minPrice', 'maxPrice', 'bedrooms',
  'suites', 'bathrooms', 'parkingSpaces', 'minArea', 'maxArea', 'orderBy',
  'condominiumId', 'condominiumName', 'propertyStatus',
];

/**
 * Monta filtros a partir dos slugs do path + query string.
 * Path tem prioridade para cidade, bairro e tipo.
 */
export function parseListingFiltersFromUrl(
  { citySlug = null, neighborhoodSlug = null, typeSlug = null } = {},
  searchParams,
  { cities = [], neighborhoods = [] } = {}
) {
  const filters = {};

  LISTING_QUERY_FILTER_KEYS.forEach((key) => {
    const value = searchParams.get(key);
    if (value != null && String(value).trim() !== '') {
      filters[key] = String(value).trim();
    }
  });
  if (searchParams.get('condominiumId')) {
    filters.condominiumLink = 'true';
  }

  if (citySlug) {
    const city = cities.find((c) => slugify(c) === citySlug);
    filters.location = city || citySlug;
  }
  if (neighborhoodSlug) {
    const nb = neighborhoods.find((n) => slugify(n) === neighborhoodSlug);
    filters.neighborhood = nb || neighborhoodSlug;
  }
  if (typeSlug) {
    const mappedType = getPropertyTypeBySlug(typeSlug);
    if (mappedType) filters.type = mappedType;
  }

  return filters;
}

export function parseListingRouteParams(params) {
  const { citySlug, neighborhoodSlug, typeSlug } = params || {};
  return { citySlug, neighborhoodSlug, typeSlug };
}

export function findCityBySlug(cities, slug) {
  return cities.find((c) => slugify(c) === slug) || null;
}

export function findNeighborhoodBySlug(properties, city, slug) {
  const match = properties.find(
    (p) => p.city === city && slugify(p.neighborhood) === slug
  );
  return match?.neighborhood || null;
}

export function findTypeBySlug(types, slug) {
  return types.find((t) => slugify(t) === slug) || null;
}
